/**
 * API Client for Operations Center
 *
 * Handles authenticated HTTP requests to the backend API.
 * Automatically injects Azure AD access tokens in Authorization headers.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiResponse<T = any> {
  data: T;
  meta?: {
    total?: number;
    limit?: number;
    offset?: number;
  };
}

export interface ApiErrorResponse {
  error: {
    code: string;
    message: string;
    details?: any;
  };
}

class ApiClient {
  private baseUrl: string;
  private getAccessToken: (() => Promise<string>) | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  /**
   * Set the function to retrieve access tokens
   */
  setAccessTokenProvider(provider: () => Promise<string>) {
    this.getAccessToken = provider;
  }

  /**
   * Make an authenticated HTTP request
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    // Get access token if available
    let accessToken: string | null = null;
    if (this.getAccessToken) {
      try {
        accessToken = await this.getAccessToken();
      } catch (error) {
        console.error('Failed to get access token:', error);
        throw new ApiError(401, 'UNAUTHORIZED', 'Authentication required');
      }
    }

    // Build headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    // Make request
    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle errors
    if (!response.ok) {
      let errorData: ApiErrorResponse;
      try {
        errorData = await response.json();
      } catch {
        throw new ApiError(
          response.status,
          'UNKNOWN_ERROR',
          `HTTP ${response.status}: ${response.statusText}`
        );
      }

      throw new ApiError(
        response.status,
        errorData.error.code,
        errorData.error.message,
        errorData.error.details
      );
    }

    // Parse response
    const data: ApiResponse<T> = await response.json();
    return data.data;
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
    const queryString = params
      ? '?' + new URLSearchParams(params).toString()
      : '';
    return this.request<T>(`${endpoint}${queryString}`, {
      method: 'GET',
    });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, body?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, body?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, body?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }

  /**
   * Multipart file upload — does NOT set Content-Type so browser adds the boundary
   */
  async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    let accessToken: string | null = null;
    if (this.getAccessToken) {
      try {
        accessToken = await this.getAccessToken();
      } catch {
        throw new ApiError(401, 'UNAUTHORIZED', 'Authentication required');
      }
    }

    const headers: Record<string, string> = {};
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

    const response = await fetch(url, { method: 'POST', headers, body: formData });

    if (!response.ok) {
      let errorData: ApiErrorResponse;
      try {
        errorData = await response.json();
      } catch {
        throw new ApiError(response.status, 'UNKNOWN_ERROR', `HTTP ${response.status}: ${response.statusText}`);
      }
      throw new ApiError(response.status, errorData.error.code, errorData.error.message, errorData.error.details);
    }

    const data: ApiResponse<T> = await response.json();
    return data.data;
  }
}

export const api = new ApiClient(API_BASE_URL);
